import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import { DropOffDayRange, DropOffSort } from "../../models/DropOffPlanner";
import classNames from "../../utils/classNames";

interface DropOffPlannerToolbarProps {
  sort: DropOffSort;
  setSort: (sort: DropOffSort) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  dayRange: DropOffDayRange;
  setDayRange: (range: DropOffDayRange) => void;
  selectedDate: DateTime;
  setSelectedDate: (date: DateTime) => void;
  onClearAll: () => void;
  isClearing: boolean;
  hideClear?: boolean;
}

const sortOptions: { value: DropOffSort, label: string }[] = [
  { value: "Oldest", label: "Oldest first" },
  { value: "Newest", label: "Newest first" },
];

const dayRangeOptions: DropOffDayRange[] = [1, 3, 5, 7];

const labelClass = "block text-[11px] uppercase tracking-wide text-gray-500 mb-0.5";
const selectClass =
  "text-sm border border-gray-300 rounded px-2 pr-5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500";

export default function DropOffPlannerToolbar({
  sort,
  setSort,
  searchTerm,
  setSearchTerm,
  dayRange,
  setDayRange,
  selectedDate,
  setSelectedDate,
  onClearAll,
  isClearing,
  hideClear,
}: DropOffPlannerToolbarProps) {
  const today = DateTime.local().startOf("day");
  const isToday = selectedDate.hasSame(today, "day");

  const rangeLabel =
    dayRange === 1
      ? selectedDate.toFormat("ccc d LLL")
      : `${selectedDate.toFormat("d LLL")} - ${selectedDate
          .plus({ days: dayRange - 1 })
          .toFormat("d LLL")}`;

  const handleDateInput = (value: string) => {
    const parsed = DateTime.fromISO(value);
    if (parsed.isValid) {
      setSelectedDate(parsed.startOf("day"));
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md px-3 py-2 flex flex-wrap items-end gap-x-4 gap-y-2">
      <div>
        <label className={labelClass}>Sort</label>
        <select
          className={selectClass}
          value={sort}
          onChange={(e) => setSort(e.target.value as DropOffSort)}
        >
          {sortOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 min-w-[200px]">
        <label className={labelClass}>Search</label>
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            className={classNames(selectClass, "w-full pl-7")}
            placeholder="VRM, make / model, source, postcode"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Planner days</label>
        <div className="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
          {dayRangeOptions.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDayRange(d)}
              className={classNames(
                "px-2 py-1 border-l border-gray-300 first:border-l-0",
                dayRange === d
                  ? "bg-primary-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              )}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="ml-auto">
        <label className={labelClass}>Date</label>
        <div className="inline-flex items-center gap-x-1">
          <button
            type="button"
            onClick={() => setSelectedDate(selectedDate.minus({ days: 1 }))}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            title="Previous day"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate(today)}
            disabled={isToday}
            className={classNames(
              "text-xs px-2 py-1 rounded border",
              isToday
                ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
            )}
          >
            Today
          </button>
          <span className="text-sm text-gray-700 px-2 min-w-[120px] text-center">
            {rangeLabel}
          </span>
          <button
            type="button"
            onClick={() => setSelectedDate(selectedDate.plus({ days: 1 }))}
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            title="Next day"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          <input
            type="date"
            value={selectedDate.toISODate() ?? ""}
            onChange={(e) => handleDateInput(e.target.value)}
            className={selectClass}
          />
        </div>
      </div>

      {!hideClear && (
        <div className="flex items-end gap-x-2">
          <button
            type="button"
            onClick={onClearAll}
            disabled={isClearing}
            className="inline-flex items-center gap-x-1 text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
